import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, FileText, Disc } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtMoney, fmtDate } from "@/lib/format";

const STATUS_META = {
  paid: { label: "Paid", color: "#0A7D5A", bg: "bg-emerald-50", text: "text-emerald-700" },
  pending: { label: "Pending (AP requested)", color: "#D97706", bg: "bg-amber-50", text: "text-amber-700" },
  bookmarked: { label: "Bookmarked (planned)", color: "#6366F1", bg: "bg-indigo-50", text: "text-indigo-700" },
  remaining: { label: "Remaining — not yet requested", color: "#64748B", bg: "bg-slate-100", text: "text-slate-700" },
  other: { label: "Reported (not counted)", color: "#94A3B8", bg: "bg-neutral-100", text: "text-neutral-600" },
  excluded: { label: "Excluded (not in totals)", color: "#9CA3AF", bg: "bg-neutral-100", text: "text-neutral-500" },
};

function Line({ it }) {
  const m = STATUS_META[it.status] || STATUS_META.other;
  return (
    <div data-testid={`bucket-line-${it.id}`} className="flex items-start justify-between gap-3 border-b border-neutral-100 py-3 last:border-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-neutral-800">{it.vendor || it.description || "—"}</p>
        {it.vendor && it.description ? <p className="truncate text-xs text-neutral-500">{it.description}</p> : null}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-400">
          {it.date ? <span>{fmtDate(it.date)}</span> : null}
          {it.album ? <span className="inline-flex items-center gap-1"><Disc className="h-3 w-3" /> {it.album}</span> : null}
          {it.ap_reference ? <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> AP #{it.ap_reference}</span> : null}
          {(it.invoice_names || []).slice(0, 1).map((n) => <span key={n} className="truncate max-w-[160px]">{n}</span>)}
          {it.source_url ? <a href={it.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#35606E] hover:underline"><ExternalLink className="h-3 w-3" /> source</a> : null}
        </div>
        {it.note ? <p className="mt-1 text-[11px] italic text-neutral-400">{it.note}</p> : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="num-display text-sm font-bold text-neutral-900">{fmtMoney(it.amount_cents)}</p>
        <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.bg} ${m.text}`}>{m.label}</span>
      </div>
    </div>
  );
}

function Group({ status, group }) {
  if (!group || !group.items.length) return null;
  const m = STATUS_META[status];
  return (
    <section data-testid={`bucket-group-${status}`} className="pb-card mt-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: m.color }} /> {m.label}
        </h2>
        <span data-testid={`bucket-subtotal-${status}`} className="num-display text-sm font-bold">{fmtMoney(group.subtotal_cents)}</span>
      </div>
      <div className="mt-2">{group.items.map((it) => <Line key={it.id} it={it} />)}</div>
    </section>
  );
}

export default function BudgetBucket() {
  const { artistId, bucket } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/budget/artists/${artistId}/bucket/${bucket}`)
      .then((r) => setD(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Failed to load bucket"); setD(false); });
  }, [artistId, bucket]);

  if (d === null)
    return <div className="min-h-screen"><TopBar /><main className="mx-auto max-w-2xl px-4 py-8"><Skeleton className="h-96 rounded-2xl" /></main></div>;
  if (d === false)
    return <div className="min-h-screen"><TopBar /><main className="mx-auto max-w-2xl px-4 py-8 text-center text-neutral-500">Bucket not found.</main></div>;

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
        <button data-testid="bucket-back" onClick={() => navigate(`/budget/${artistId}`)} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-[#35606E] hover:underline">
          <ArrowLeft className="h-4 w-4" /> {d.artist.name} budget
        </button>

        <div className="pb-card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">{d.artist.name}</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{d.label}</h1>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Stat label="Committed" value={fmtMoney(d.committed_cents)} />
            <Stat label="Budget" value={d.budget_cents == null ? "—" : fmtMoney(d.budget_cents)} />
            <Stat label="Paid" value={fmtMoney(d.groups.paid.subtotal_cents)} />
            <Stat label="Pending" value={fmtMoney(d.groups.pending.subtotal_cents)} />
          </div>
        </div>

        <Group status="paid" group={d.groups.paid} />
        <Group status="pending" group={d.groups.pending} />
        <Group status="bookmarked" group={d.groups.bookmarked} />
        <Group status="remaining" group={d.remaining} />
        <Group status="other" group={d.other} />
        <Group status="excluded" group={d.excluded} />

        {!d.groups.paid.items.length && !d.groups.pending.items.length && !d.groups.bookmarked.items.length && !d.remaining.items.length && !d.excluded.items.length ? (
          <p className="mt-8 text-center text-sm text-neutral-500">No line items in this bucket yet.</p>
        ) : null}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</p>
      <p className="num-display text-base font-bold">{value}</p>
    </div>
  );
}
